import dayjs from 'dayjs';
import { FilterReportType, MultiselectOption } from '@cbo/shared-library';
import { today, yesterday } from '../utils/reportingUtils/filterUtils';
import { DateComparisonRanges, SingleSelectGroup } from '../components/GlobalFilterBar/types';
import { DetailsAndConfig, OrganizationUser } from '../admin/requests/index';
import { ExportScheduleReportFileFormat } from '../../sales/enums';

export enum ReportsType {
  APPROACHING_ACA_THRESHOLD = 'approachingACAThreshold',
  APPROACHING_OVERTIME_THRESHOLD = 'approachingOvertimeThreshold',
  EMPLOYEE_BREAK = 'employeeBreak',
  EMPLOYEE_PAYROLL = 'employeePayroll',
  EMPLOYEE_SALES_PERFORMANCE = 'employeeSalesPerformance',
  GENERIC_PAYROLL_EXPORT = 'genericPayrollExport',
  EMPLOYEE_TIP = 'employeeTip',
  INTERVAL_SALES_AND_LABOR = 'intervalSalesAndLabor',
  SALES_SUMMARY = 'salesSummary',
  PRODUCT_MIX = 'productMix',
  PAYMENTS = 'payments',
  TRANSACTIONS = 'transactions',
}

export enum SubReportsType {
  APPROACHING_HOURLY_THRESHOLD = 'approachingHourlyThreshold',
  EMPLOYEE_AVERAGE_HOURS_WORKED = 'employeeAverageHoursWorked',
  EMPLOYEE_PAYROLL_SUMMARY = 'employeePayrollSummary',
  EMPLOYEE_PAYROLL_DETAILS = 'employeePayrollDetails',
  PRODUCT_MIX_MENU_ITEMS = 'productMixMenuItems',
  PRODUCT_MIX_EMPLOYEE_SALES = 'productMixEmployeeSales',
}

export enum ScheduledReportsTabId {
  All = 'all',
  ScheduledByYou = 'scheduled-by-you',
  SentToYou = 'sent-to-you',
}

export interface FilterFormValues {
  statuses: MultiselectOption[];
  occurrences: MultiselectOption[];
  sites: MultiselectOption[];
  scheduledBy: MultiselectOption[];
}

export enum ReportSettingsFieldType {
  AUTOCOMPLETE = 'autocomplete',
  DATE_RANGE = 'dateRange',
  DATE_RANGE_COMPARE = 'dateRangeCompare',
  MULTI_SELECT = 'multiSelect',
  SINGLE_SELECT = 'singleSelect',
  SITE_SELECT = 'siteSelect',
}

export enum PredefinedDateRangeType {
  TODAY = 'today',
  YESTERDAY = 'yesterday',
  WEEK = 'week',
  LAST_WEEK = 'lastWeek',
  MONTH = 'month',
  LAST_MONTH = 'lastMonth',
  YEAR = 'year',
  LAST_YEAR = 'lastYear',
  FISCAL_WEEK = 'fiscalWeek',
  LAST_FISCAL_WEEK = 'lastFiscalWeek',
  FISCAL_MONTH = 'fiscalMonth',
  LAST_FISCAL_MONTH = 'lastFiscalMonth',
  FISCAL_YEAR = 'fiscalYear',
  LAST_FISCAL_YEAR = 'lastFiscalYear',
}

export enum PredefinedDateRangeChipsType {
  TODAY = 'today',
  PAYROLL = 'payroll',
  WORK_WEEK = 'workWeek',
}

export enum ReoccurrenceType {
  NO_REPEAT = 'NONE',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

export type OptionType = {
  id: string;
  label: string;
  key?: string;
  title?: string;
  value?: string;
};

export type ReportFieldType = OptionType & {
  reportType: 'predefined' | 'custom';
};

export type defaultValueType = {
  sites?: string[];
  datePickerNav?: dayjs.Dayjs;
  dateRange?:
    | {
        startDate: string;
        endDate: string;
      }
    | OptionType
    | null;
  jobType?: [];
  employeeStatus?: [];
  hourlyThresholds?: [];
  jobCodeNames?: [];
  jobcodes?: [];
  dayParts?: [];
  employees?: [];
  laborReportingGroup?: [];
  granularity?: [];
  comparedDateRange?:
    | {
        startDate: string;
        endDate: string;
        label: string;
      }
    | OptionType
    | null;
  categories?: [];
  items?: [];
  paymentType?: [];
  transactionContains?: [];
};

/**
 * Represents the configuration for report settings filters.
 * This type defines the structure and properties used to configure
 * various filters in the report settings.
 *
 * @property id - A unique identifier for the filter.
 * @property columnName - The name of the column associated with the filter.
 * @property required - Indicates whether the filter is mandatory.
 * @property type - The type of the filter, defined by `ReportSettingsFieldType`.
 *
 * @property isSingleSiteSelect - (Optional) Specifies if the filter is a single site selector.
 *
 * @property queryFilterType - (Optional) The type of query filter, defined by `FilterReportType`.
 *
 * @property isWeeklyDateRangeSelector - (Optional) Specifies if the filter is a weekly date range selector.
 * @property isPredefinedDateRangeSelector - (Optional) use predefined date range selector.
 * @property useChips - (Optional) Indicates if chips are used for date range selection.
 * @property dateRangeChips - (Optional) Predefined date range chips, defined by `PredefinedDateRangeChipsType[]`.
 *
 * @property defaultComparisonRange - (Optional) The default number of comparison ranges.
 * @property comparisonRanges - (Optional) The available comparison ranges, defined by `DateComparisonRanges[]`.
 *
 * @property options - (Optional) General options for the filter, which can be a group of single select options
 * or an array of options of type `OptionType<string>`.
 */
export type ReportSettingsFiltersType = {
  isRequired?: boolean;
  field?: string;
  id: number;
  columnName: string;
  required: boolean;
  type: ReportSettingsFieldType;
  isSingleSiteSelect?: boolean;
  queryFilterType?: FilterReportType;
  isWeeklyDateRangeSelector?: boolean;
  isPredefinedDateRangeSelector?: boolean;
  useChips?: boolean;
  dateRangeChips?: PredefinedDateRangeChipsType[];
  defaultComparisonRange?: number;
  comparisonRanges?: DateComparisonRanges[];
  options?: SingleSelectGroup[] | OptionType[] | MultiselectOption[];
};

export type ReportSettingsType = {
  id: string;
  category: string;
  filters: ReportSettingsFiltersType[];
  defaultValue: defaultValueType;
};

export type ReportSettingsConfigType = Record<string, ReportSettingsType[]>;

// Currently, we are using a static configuration object to handle field rendering in the front-end as a temporary solution.
// In the future, we plan to render these fields dynamically using JSON data retrieved from the backend.

const employeePayrollFilters: ReportSettingsFiltersType[] = [
  {
    id: 1,
    columnName: 'sites',
    required: true,
    type: ReportSettingsFieldType.SITE_SELECT,
  },
  {
    id: 2,
    columnName: 'dateRange',
    required: true,
    type: ReportSettingsFieldType.DATE_RANGE,
    dateRangeChips: [PredefinedDateRangeChipsType.WORK_WEEK, PredefinedDateRangeChipsType.PAYROLL],
    useChips: true,
  },
  {
    id: 3,
    columnName: 'jobCodeNames',
    required: false,
    type: ReportSettingsFieldType.MULTI_SELECT,
    queryFilterType: FilterReportType.PAYROLL_EXCLUDE_JOBCODE,
  },
];

const employeePayrollDefaultValues: defaultValueType = {
  sites: [],
  dateRange: {
    endDate: today,
    startDate: today,
  },
  jobCodeNames: [],
};

export const ReportSettingsConfig: ReportSettingsConfigType = {
  [ReportsType.EMPLOYEE_PAYROLL]: [
    {
      id: SubReportsType.EMPLOYEE_PAYROLL_SUMMARY,
      category: ReportsType.EMPLOYEE_PAYROLL,
      filters: employeePayrollFilters,
      defaultValue: employeePayrollDefaultValues,
    },
    {
      id: SubReportsType.EMPLOYEE_PAYROLL_DETAILS,
      category: ReportsType.EMPLOYEE_PAYROLL,
      filters: employeePayrollFilters,
      defaultValue: employeePayrollDefaultValues,
    },
  ],
  [ReportsType.GENERIC_PAYROLL_EXPORT]: [
    {
      id: ReportsType.GENERIC_PAYROLL_EXPORT,
      category: ReportsType.GENERIC_PAYROLL_EXPORT,
      filters: [
        {
          id: 1,
          columnName: 'sites',
          required: true,
          type: ReportSettingsFieldType.SITE_SELECT,
        },
        {
          id: 2,
          columnName: 'dateRange',
          required: true,
          type: ReportSettingsFieldType.DATE_RANGE,
          dateRangeChips: [PredefinedDateRangeChipsType.PAYROLL],
          useChips: true,
        },
      ],
      defaultValue: {
        sites: [],
        dateRange: {
          endDate: today,
          startDate: today,
        },
      },
    },
  ],
  [ReportsType.EMPLOYEE_TIP]: [
    {
      id: ReportsType.EMPLOYEE_TIP,
      category: ReportsType.EMPLOYEE_TIP,
      filters: [
        {
          id: 1,
          columnName: 'sites',
          required: true,
          type: ReportSettingsFieldType.SITE_SELECT,
        },
        {
          id: 2,
          columnName: 'dateRange',
          required: true,
          type: ReportSettingsFieldType.DATE_RANGE,
          dateRangeChips: [PredefinedDateRangeChipsType.WORK_WEEK],
          useChips: true,
        },
        {
          id: 3,
          columnName: 'employees',
          required: false,
          type: ReportSettingsFieldType.MULTI_SELECT,
          queryFilterType: FilterReportType.EMPLOYEE_PAYMENTS,
        },
      ],
      defaultValue: {
        sites: [],
        dateRange: {
          endDate: today,
          startDate: today,
        },
        employees: [],
      },
    },
  ],
  [ReportsType.SALES_SUMMARY]: [
    {
      id: ReportsType.SALES_SUMMARY,
      category: ReportsType.SALES_SUMMARY,
      filters: [
        {
          id: 1,
          columnName: 'sites',
          required: true,
          type: ReportSettingsFieldType.SITE_SELECT,
        },
        {
          id: 2,
          columnName: 'dateRange',
          required: true,
          type: ReportSettingsFieldType.DATE_RANGE,
          isPredefinedDateRangeSelector: true,
        },
      ],
      defaultValue: {
        sites: [],
        dateRange: {
          endDate: yesterday,
          startDate: yesterday,
        },
      },
    },
  ],
  [ReportsType.PRODUCT_MIX]: [
    {
      id: SubReportsType.PRODUCT_MIX_MENU_ITEMS,
      category: ReportsType.PRODUCT_MIX,
      filters: [
        {
          id: 1,
          columnName: 'sites',
          required: true,
          type: ReportSettingsFieldType.SITE_SELECT,
        },
        {
          id: 2,
          columnName: 'dateRange',
          required: true,
          type: ReportSettingsFieldType.DATE_RANGE,
          isPredefinedDateRangeSelector: true,
        },
        {
          id: 3,
          columnName: 'comparedDateRange',
          required: true,
          type: ReportSettingsFieldType.DATE_RANGE_COMPARE,
          defaultComparisonRange: 0,
        },
        {
          id: 4,
          columnName: 'categories',
          required: false,
          type: ReportSettingsFieldType.MULTI_SELECT,
          queryFilterType: FilterReportType.CATEGORY,
        },
        {
          id: 5,
          columnName: 'items',
          required: false,
          type: ReportSettingsFieldType.MULTI_SELECT,
          queryFilterType: FilterReportType.ITEM,
        },
      ],
      defaultValue: {
        sites: [],
        dateRange: {
          endDate: yesterday,
          startDate: yesterday,
        },
        comparedDateRange: {
          startDate: dayjs(yesterday).subtract(1, 'd').format('L'),
          endDate: dayjs(yesterday).subtract(1, 'd').format('L'),
          label: 'previousPeriod',
        },
        categories: [],
        items: [],
      },
    },
    {
      id: SubReportsType.PRODUCT_MIX_EMPLOYEE_SALES,
      category: ReportsType.PRODUCT_MIX,
      filters: [
        {
          id: 1,
          columnName: 'sites',
          required: true,
          type: ReportSettingsFieldType.SITE_SELECT,
        },
        {
          id: 2,
          columnName: 'dateRange',
          required: true,
          type: ReportSettingsFieldType.DATE_RANGE,
          isPredefinedDateRangeSelector: true,
        },
        {
          id: 3,
          columnName: 'categories',
          required: false,
          type: ReportSettingsFieldType.MULTI_SELECT,
          queryFilterType: FilterReportType.CATEGORY,
        },
        {
          id: 4,
          columnName: 'items',
          required: false,
          type: ReportSettingsFieldType.MULTI_SELECT,
          queryFilterType: FilterReportType.ITEM,
        },
        {
          id: 5,
          columnName: 'employees',
          required: false,
          type: ReportSettingsFieldType.MULTI_SELECT,
          queryFilterType: FilterReportType.EMPLOYEE_PRODUCT_MIX,
        },
      ],
      defaultValue: {
        sites: [],
        dateRange: {
          endDate: yesterday,
          startDate: yesterday,
        },
        comparedDateRange: {
          startDate: dayjs(yesterday).subtract(1, 'd').format('L'),
          endDate: dayjs(yesterday).subtract(1, 'd').format('L'),
          label: 'previousPeriod',
        },
        categories: [],
        items: [],
        employees: [],
      },
    },
  ],
  [ReportsType.PAYMENTS]: [
    {
      id: ReportsType.PAYMENTS,
      category: ReportsType.PAYMENTS,
      filters: [
        {
          id: 1,
          columnName: 'sites',
          required: true,
          type: ReportSettingsFieldType.SITE_SELECT,
        },
        {
          id: 2,
          columnName: 'dateRange',
          required: true,
          type: ReportSettingsFieldType.DATE_RANGE,
          isPredefinedDateRangeSelector: true,
        },
        {
          id: 3,
          columnName: 'comparedDateRange',
          required: true,
          type: ReportSettingsFieldType.DATE_RANGE_COMPARE,
          defaultComparisonRange: 0,
        },
        {
          id: 4,
          columnName: 'employees',
          required: false,
          type: ReportSettingsFieldType.MULTI_SELECT,
          queryFilterType: FilterReportType.EMPLOYEE_PAYMENTS,
        },
        {
          id: 5,
          columnName: 'paymentType',
          required: false,
          type: ReportSettingsFieldType.MULTI_SELECT,
          queryFilterType: FilterReportType.PAYMENT_TYPES,
        },
      ],
      defaultValue: {
        sites: [],
        dateRange: {
          endDate: yesterday,
          startDate: yesterday,
        },
        comparedDateRange: {
          startDate: dayjs(yesterday).subtract(1, 'd').format('L'),
          endDate: dayjs(yesterday).subtract(1, 'd').format('L'),
          label: 'previousPeriod',
        },
        employees: [],
        paymentType: [],
      },
    },
  ],
  [ReportsType.TRANSACTIONS]: [
    {
      id: ReportsType.TRANSACTIONS,
      category: ReportsType.TRANSACTIONS,
      filters: [
        {
          id: 1,
          columnName: 'sites',
          required: true,
          type: ReportSettingsFieldType.SITE_SELECT,
        },
        {
          id: 2,
          columnName: 'dateRange',
          required: true,
          type: ReportSettingsFieldType.DATE_RANGE,
          isPredefinedDateRangeSelector: true,
        },
        {
          id: 3,
          columnName: 'transactionContains',
          required: false,
          type: ReportSettingsFieldType.MULTI_SELECT,
        },
      ],
      defaultValue: {
        sites: [],
        dateRange: {
          endDate: yesterday,
          startDate: yesterday,
        },
        transactionContains: [],
      },
    },
  ],
};

export enum PredefinedReportKeys {
  EMPLOYEE_PAYROLL = 'employee_payroll_report',
  GENERIC_PAYROLL_EXPORT = 'generic_payroll_export',
  EMPLOYEE_TIP = 'employee_tip_report',
  SALES_SUMMARY = 'sales_summary',
  PRODUCT_MIX = 'product_mix_report',
  PAYMENTS = 'payments_report',
  TRANSACTIONS = 'transaction_summary',
}

export interface ScheduleReportsPayload {
  scheduleName: string;
  reportName: string;
  description: string;
  recipients?: { userId: string; userName: string }[];
  reportDefinition: { assignedStoreIds: string[]; destinationUrl: string; detailsAndConfig: DetailsAndConfig | null };
  startDate: string;
  runTime: string;
  timezone: string;
  deliveryFormat: string;
  isSecurityEnabled: boolean;
  reportSettings: { [key: string]: ReportSettingsType[] };
  recurrenceType?: string;
  daysOfWeek?: string[];
  endDate?: string;
  reportId?: string;
}

export interface TransformedFilterSettings {
  id: number;
  columnName: string;
  required: boolean;
  type: string;
  isPredefinedDateRangeSelector?: boolean;
  useChips?: boolean;
  dateRangeChips?: string[];
  defaultComparisonRange?: string | number;
  queryFilterType?: string;
}

export interface TransformedReportSettings {
  id: string;
  category: string;
  filters: TransformedFilterSettings[];
  defaultValue: Record<string, unknown>;
}

export type TransformedReportSettingsConfig = Record<string, TransformedReportSettings[]>;

// sites, dateRange
interface FilterList {
  field: string;
  header: string;
  type: string;
  queryFilterType?: string;
  isRequired?: boolean;
  dateRangeChips?: string[];
  defaultComparisonRange?: number;
}
interface Header {
  field: string;
  header: string;
}
interface HeadersList {
  [header: string]: Header[] | Header;
}
type HeadersListOrArray = HeadersList | Header[];
interface Reports {
  filters: FilterList[];
  headers: HeadersListOrArray;
  defaultValue?: Record<string, unknown>;
}

export interface PredefinedReports {
  reportHeaders: {
    [reportType: string]: Array<{ [subReportName: string]: Reports }>;
  };
  reportTypes: string[];
}

export enum ScheduleStatus {
  SCHEDULED = 'SCHEDULED',
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  COMPLETED = 'COMPLETED',
  EXPIRED = 'EXPIRED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
  EXPIRING_SOON = 'EXPIRING_SOON',
}
export type ScheduleOccurrence = 'Daily' | 'Weekly' | 'Monthly';

export interface ScheduledReport {
  id: string;
  scheduleName: string;
  reportName: string;
  startDate: string;
  endDate: string | null;
  runTime: string;
  recurrenceType: ReoccurrenceType | string;
  dayOfMonth?: number | null;
  daysOfWeek?: string[] | null;
  storeIds: string[];
  recipients: OrganizationUser[];
  scheduledBy: string;
  scheduleStatus: ScheduleStatus;
  description?: string;
  isSecurityEnabled?: boolean;
  reportSettings?: Record<string, unknown>;
  timezone?: string;
  deliveryFormat?: ExportScheduleReportFileFormat;
  reportDefinition?: { assignedStoreIds: string[]; destinationUrl: string };
  customFields?: Record<string, unknown>;
}
